import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import {useSelector} from "react-redux";
import NumberFormat from "react-number-format";
import {PlusIcon, RefreshIcon} from "@heroicons/react/outline";

import FormPopup from "../../../components/common/popup/form-popup";
import LoaderWrapper from "../../../components/common/loader/loader-wrapper";
import {getProductPurchaseHistory} from "../../../api/catalog";
import {getFormattedDate} from "../../../utils/timeUtils";
import DocumentBodyEditableFormattedNumberInput from "./document-body-editable-number-input";
import DocumentBodyEditableDiscountListInput from "./document-body-editable-discount-input";
import {applyDiscountsToValue, formatDiscountLabel, normalizeDiscountList} from "./document-body-utils";

const formatAmount = (value, suffix) => {
    if (value === null || value === undefined || value === "") {
        return "-";
    }
    return (
        <NumberFormat
            value={value}
            displayType="text"
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={4}
            suffix={suffix ? ` ${suffix.trim()}` : ""}
        />
    );
};

const HistoryEntry = ({entry, suffix, t}) => {
    const userName = (() => {
        const firstName = entry?.user?.firstname ?? "";
        const lastName = entry?.user?.lastname ?? "";
        return `${firstName} ${lastName}`.trim() || "-";
    })();
    const dateLabel = entry?.date ? getFormattedDate(entry.date) : "-";
    const discountLabel = (() => {
        const discounts = entry?.discount;
        if (!discounts) {
            return "-";
        }
        if (Array.isArray(discounts)) {
            return discounts.length > 0 ? discounts.map((value) => formatDiscountLabel(value)).join(" + ") : "-";
        }
        if (typeof discounts === "string") {
            return discounts.trim() !== "" ? discounts : "-";
        }
        return "-";
    })();

    return (
        <li className="relative ">
            <div className="rounded-lg border border-gray-100 bg-white/90 p-4 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <p className="text-sm font-semibold text-gray-900">{userName}</p>
                        <p className="text-xs text-gray-500">{dateLabel}</p>
                    </div>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        {t("app.quotations.purchase_history", {defaultValue: "Storico"})}
                    </span>
                </div>
                <dl className="mt-3 grid gap-3 text-sm text-gray-900 sm:grid-cols-2">
                    <div>
                        <dt className="text-xs uppercase tracking-wide text-gray-500">{t("app.quotations.purchase_price")}</dt>
                        <dd className="font-semibold">{formatAmount(entry?.purchase_price, suffix)}</dd>
                    </div>
                    <div>
                        <dt className="text-xs uppercase tracking-wide text-gray-500">{t("app.products.list_price")}</dt>
                        <dd className="font-semibold">{formatAmount(entry?.list_price, suffix)}</dd>
                    </div>
                    <div>
                        <dt className="text-xs uppercase tracking-wide text-gray-500">{t("app.quotations.discounts")}</dt>
                        <dd className="font-semibold">{discountLabel}</dd>
                    </div>
                    <div>
                        <dt className="text-xs uppercase tracking-wide text-gray-500">{t("app.common.date", {defaultValue: "Data"})}</dt>
                        <dd className="font-semibold">{dateLabel}</dd>
                    </div>
                </dl>
                {entry?.price_note ? (
                    <p className="mt-3 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
                        <span className="font-semibold text-gray-900">{t("app.products.price_note")}:</span>
                        <span className="ml-1">{entry.price_note}</span>
                    </p>
                ) : null}
            </div>
        </li>
    );
};

HistoryEntry.propTypes = {
    entry: PropTypes.object.isRequired,
    suffix: PropTypes.string,
    t: PropTypes.func.isRequired,
};

HistoryEntry.defaultProps = {
    suffix: "",
};

const DocumentBodyPurchasePriceHistoryPopup = ({
    isOpen,
    onClose,
    product,
    purchasePrice,
    t,
    suffix,
    listPrice,
    listPriceDiscounts,
    onListPriceCommit,
    onListPriceDiscountsCommit,
    isEditable,
    canShowCatalogAction,
    onAddCatalog,
    canViewHistory,
}) => {
    const {loggedCompany} = useSelector((state) => state.user);
    const [historyEntries, setHistoryEntries] = useState([]);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState(null);

    const normalizedDiscounts = useMemo(
        () => normalizeDiscountList(listPriceDiscounts),
        [listPriceDiscounts]
    );

    const normalizedListPrice = useMemo(() => {
        const candidate = listPrice ?? product?.list_price ?? 0;
        const numeric = Number(candidate);
        return Number.isFinite(numeric) ? numeric : 0;
    }, [listPrice, product?.list_price]);

    const previewNetPrice = useMemo(
        () => applyDiscountsToValue(normalizedListPrice, normalizedDiscounts),
        [normalizedListPrice, normalizedDiscounts]
    );

    const latestListPriceRef = useRef(normalizedListPrice);
    const latestDiscountsRef = useRef(normalizedDiscounts);

    useEffect(() => {
        latestListPriceRef.current = normalizedListPrice;
    }, [normalizedListPrice]);

    useEffect(() => {
        latestDiscountsRef.current = normalizedDiscounts;
    }, [normalizedDiscounts]);

    const fetchHistory = useCallback(
        (signal) => {
            if (!isOpen || !product?.id || !loggedCompany?.id || !canViewHistory) {
                setHistoryEntries([]);
                setHistoryError(null);
                setIsHistoryLoading(false);
                return Promise.resolve();
            }

            setIsHistoryLoading(true);
            setHistoryError(null);

            return getProductPurchaseHistory({
                company_id: loggedCompany.id,
                product_id: product.id,
                signal,
            })
                .then((response) => {
                    setHistoryEntries(Array.isArray(response) ? response : []);
                })
                .catch((error) => {
                    if (error?.name === "AbortError") {
                        return;
                    }
                    setHistoryEntries([]);
                    setHistoryError(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
                })
                .finally(() => {
                    if (!signal || !signal.aborted) {
                        setIsHistoryLoading(false);
                    }
                });
        },
        [canViewHistory, isOpen, loggedCompany?.id, product?.id, t]
    );

    useEffect(() => {
        const controller = new AbortController();
        fetchHistory(controller.signal);
        return () => {
            controller.abort();
        };
    }, [fetchHistory]);

    const timelineEntries = useMemo(() => {
        if (historyEntries.length > 0) {
            return [...historyEntries].sort((a, b) => {
                const timeA = a?.date ? new Date(a.date).getTime() : 0;
                const timeB = b?.date ? new Date(b.date).getTime() : 0;
                return timeB - timeA;
            });
        }
        if (!product) {
            return [];
        }
        return [
            {
                id: "current-product",
                purchase_price: product?.purchase_price ?? null,
                list_price: product?.list_price ?? null,
                discount: product?.list_price_discounts ?? product?.discount ?? [],
                date: product?.last_price_date ?? null,
                price_note: product?.price_note ?? null,
                user: product?.last_price_user ?? null,
            },
        ];
    }, [historyEntries, product]);

    const visibleTimelineEntries = useMemo(() => timelineEntries, [timelineEntries]);

    const latestEntry = timelineEntries.length > 0 ? timelineEntries[0] : null;
    const lastUpdatedLabel = latestEntry?.date ? getFormattedDate(latestEntry.date) : null;
    const formattedPreviewPrice = formatAmount(previewNetPrice, suffix);

    const previewDelta = useMemo(() => {
        const base = Number(purchasePrice ?? product?.purchase_price);
        if (!Number.isFinite(base)) {
            return null;
        }
        const delta = Number((previewNetPrice - base).toFixed(2));
        if (!Number.isFinite(delta) || delta === 0) {
            return null;
        }
        return delta;
    }, [purchasePrice, previewNetPrice, product?.purchase_price]);

    const canRefreshHistory = canViewHistory && Boolean(product?.id);

    const handleAddCatalog = useCallback(() => {
        if (!canShowCatalogAction) {
            return;
        }
        if (typeof onAddCatalog === "function") {
            onAddCatalog();
        }
    }, [canShowCatalogAction, onAddCatalog]);

    const handleRefreshHistory = useCallback(() => {
        if (!canRefreshHistory) {
            return;
        }
        fetchHistory();
    }, [canRefreshHistory, fetchHistory]);

    const handleListPriceInputCommit = useCallback((nextValue) => {
        if (typeof onListPriceCommit !== "function") {
            return;
        }
        const numericValue = Number(nextValue);
        const sanitizedListPriceValue = Number.isFinite(numericValue) ? numericValue : 0;
        latestListPriceRef.current = sanitizedListPriceValue;
        const activeDiscounts = Array.isArray(latestDiscountsRef.current) ? latestDiscountsRef.current : [];
        const computedPurchase = applyDiscountsToValue(sanitizedListPriceValue, activeDiscounts);
        onListPriceCommit(nextValue, {
            list_price: sanitizedListPriceValue,
            list_price_discounts: activeDiscounts,
            purchase_price: computedPurchase,
        });
    }, [onListPriceCommit]);

    const handleListPriceDiscountsInputCommit = useCallback((nextValue) => {
        if (typeof onListPriceDiscountsCommit !== "function") {
            return;
        }
        const normalizedNextDiscounts = normalizeDiscountList(nextValue);
        latestDiscountsRef.current = normalizedNextDiscounts;
        const activeListPrice = latestListPriceRef.current ?? normalizedListPrice;
        const sanitizedListPriceValue = Number.isFinite(activeListPrice) ? activeListPrice : 0;
        const computedPurchase = applyDiscountsToValue(sanitizedListPriceValue, normalizedNextDiscounts);
        onListPriceDiscountsCommit(nextValue, {
            list_price: sanitizedListPriceValue,
            list_price_discounts: normalizedNextDiscounts,
            purchase_price: computedPurchase,
        });
    }, [normalizedListPrice, onListPriceDiscountsCommit]);

    return (
        <FormPopup
            title={t("app.documents.purchase_price", {defaultValue: "Prezzo acquisto"})}
            isOpen={isOpen}
            setIsOpen={(nextOpen) => {
                if (!nextOpen) {
                    onClose();
                }
            }}
            defaultWith="max-w-4xl"
            isActiveFormPm={false}
        >
            <div className="space-y-6 p-5">
                <div className="grid gap-6 lg:grid-cols-[1.1fr_minmax(0,0.9fr)]">
                    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <p className="text-base font-semibold text-gray-900">{t("app.quotations.purchase_history", {defaultValue: "Storico acquisti"})}</p>
                                <p className="text-xs text-gray-500">
                                    {lastUpdatedLabel
                                        ? `${t("app.common.latest_update", {defaultValue: "Ultimo aggiornamento"})}: ${lastUpdatedLabel}`
                                        : t("app.quotations.purchase_history_empty", {defaultValue: "Nessun storico disponibile"})}
                                </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    type="button"
                                    onClick={handleRefreshHistory}
                                    disabled={isHistoryLoading || !canRefreshHistory}
                                    className="inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-semibold text-gray-600 shadow-sm transition hover:border-blue-200 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    <RefreshIcon className={`mr-1 h-4 w-4 ${isHistoryLoading ? "animate-spin text-blue-600" : ""}`} aria-hidden="true" />
                                    {t("app.common.refresh", {defaultValue: "Aggiorna"})}
                                </button>
                            </div>
                        </div>
                        <div className="mt-5 max-h-[26rem] overflow-y-auto pr-2">
                            <LoaderWrapper isLoading={isHistoryLoading}>
                                {!canViewHistory ? (
                                    <p className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
                                        {t("app.documents.purchase_history_permission_denied", {defaultValue: "Listino non disponibile"})}
                                    </p>
                                ) : historyError ? (
                                    <div className="space-y-3 rounded-lg border border-dashed border-red-200 bg-red-50 p-4 text-sm text-red-700">
                                        <p>{historyError}</p>
                                        <button
                                            type="button"
                                            onClick={handleRefreshHistory}
                                            disabled={isHistoryLoading || !canRefreshHistory}
                                            className="inline-flex items-center rounded-full border border-red-200 bg-white px-3 py-1 text-xs font-semibold text-red-700 transition hover:border-red-300 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {t("app.common.try_again", {defaultValue: "Riprova"})}
                                        </button>
                                    </div>
                                ) : visibleTimelineEntries.length > 0 ? (
                                    <>
                                        <ul className="space-y-4  border-gray-200">
                                            {visibleTimelineEntries.map((entry) => (
                                                <HistoryEntry
                                                    key={entry?.id ?? `${entry?.date ?? ""}-${entry?.purchase_price ?? ""}`}
                                                    entry={entry}
                                                    suffix={suffix}
                                                    t={t}
                                                />
                                            ))}
                                        </ul>
                                    </>
                                ) : (
                                    <div className="space-y-3 rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
                                        <p>{t("app.quotations.purchase_history_empty", {defaultValue: "Nessun storico disponibile"})}</p>
                                        {canRefreshHistory ? (
                                            <button
                                                type="button"
                                                onClick={handleRefreshHistory}
                                                disabled={isHistoryLoading}
                                                className="inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-semibold text-gray-600 transition hover:border-blue-200 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
                                            >
                                                {t("app.common.refresh", {defaultValue: "Aggiorna"})}
                                            </button>
                                        ) : null}
                                    </div>
                                )}
                            </LoaderWrapper>
                        </div>
                    </div>

                    <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-5 shadow-inner">
                        <p className="text-base font-semibold text-gray-900">{t("app.documents.list_price", {defaultValue: "Prezzo di listino"})}</p>
                        <p className="text-xs text-gray-500">{t("app.documents.list_price_description", {defaultValue: "Aggiorna il listino e visualizza subito l'impatto sugli acquisti."})}</p>
                        {!isEditable ? (
                            <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                {t("app.documents.readonly_prices_hint", {defaultValue: "Non hai i permessi per modificare questi valori"})}
                            </p>
                        ) : null}
                        <div className="mt-4 space-y-4">
                            <div className="grid gap-4 md:grid-cols-1">
                                <div className="rounded-xl border border-white/60 bg-white px-4 py-3 shadow">
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                            {t("app.documents.list_price", {defaultValue: "Prezzo di listino"})}
                                        </p>
                                    </div>
                                    <div className="mt-2 flex items-center rounded-md border border-gray-200 bg-gray-50  py-1">
                                        <DocumentBodyEditableFormattedNumberInput
                                            value={listPrice ?? ""}
                                            onCommit={handleListPriceInputCommit}
                                            suffix={suffix}
                                            decimalScale={2}
                                            isEditable={isEditable}
                                            className="w-full text-right text-lg font-semibold text-gray-900"
                                        />
                                        <div className={"text-right mx-2"}>
                                            <DocumentBodyEditableDiscountListInput
                                                value={listPriceDiscounts}
                                                onCommit={handleListPriceDiscountsInputCommit}
                                                suffix={suffix}
                                                t={t}
                                                isEditable={isEditable}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="">

                                </div>
                            </div>
                            <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("app.quotations.preview", {defaultValue: "Anteprima"})}</p>
                                <div className="mt-2 flex items-baseline justify-between">
                                    <p className="text-2xl font-semibold text-gray-900">{formattedPreviewPrice}</p>
                                    {previewDelta !== null ? (
                                        <span className={previewDelta >= 0 ? "text-xs font-semibold text-red-600" : "text-xs font-semibold text-green-600"}>
                                            {previewDelta >= 0 ? "+" : ""}{previewDelta.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} {suffix}
                                        </span>
                                    ) : null}
                                </div>
                                <p className="text-xs text-gray-500">{t("app.quotations.preview_description", {defaultValue: "Basato sul listino corrente e sugli sconti applicati"})}</p>
                            </div>
                            {canShowCatalogAction ? (
                                <button
                                    type="button"
                                    onClick={handleAddCatalog}
                                    className="inline-flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                >
                                    <PlusIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                                    {t("app.products.add_in_catalog", {defaultValue: "Aggiungi al catalogo"})}
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </FormPopup>
    );
};

DocumentBodyPurchasePriceHistoryPopup.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    product: PropTypes.object,
    purchasePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    t: PropTypes.func.isRequired,
    suffix: PropTypes.string,
    listPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    listPriceDiscounts: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    ]),
    onListPriceCommit: PropTypes.func,
    onListPriceDiscountsCommit: PropTypes.func,
    isEditable: PropTypes.bool,
    canShowCatalogAction: PropTypes.bool,
    onAddCatalog: PropTypes.func,
    canViewHistory: PropTypes.bool,
};

DocumentBodyPurchasePriceHistoryPopup.defaultProps = {
    product: null,
    purchasePrice: "",
    suffix: "",
    listPrice: "",
    listPriceDiscounts: [],
    onListPriceCommit: undefined,
    onListPriceDiscountsCommit: undefined,
    isEditable: true,
    canShowCatalogAction: false,
    onAddCatalog: undefined,
    canViewHistory: true,
};

export default DocumentBodyPurchasePriceHistoryPopup;
